<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>實際點數以 Wixtar 為準；下方明細為 CRM 發放結果。若需逐筆點數流水，請至「點數交易」查詢（鏡像）或 Wixtar。
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else-if="batchIssuance" class="detail-container">
      <!-- 基本資訊 -->
      <div class="detail-section">
        <h2 class="section-title">基本資訊</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>批次 ID</label>
            <code class="code-badge">{{ batchIssuance.batchId }}</code>
          </div>
          <div class="info-item">
            <label>每位會員發放點數</label>
            <span>{{ batchIssuance.points }} 點</span>
          </div>
          <div class="info-item">
            <label>發放原因</label>
            <span>{{ batchIssuance.reason || '-' }}</span>
          </div>
          <div class="info-item">
            <label>狀態</label>
            <span :class="getStatusBadgeClass(batchIssuance.status)">
              {{ getStatusText(batchIssuance.status) }}
            </span>
          </div>
          <div class="info-item">
            <label>建立時間</label>
            <span>{{ formatDateTime(batchIssuance.createdAt) }}</span>
          </div>
          <div class="info-item">
            <label>執行時間</label>
            <span>{{ formatDateTime(batchIssuance.executedAt) || '-' }}</span>
          </div>
          <div class="info-item">
            <label>完成時間</label>
            <span>{{ formatDateTime(batchIssuance.completedAt) || '-' }}</span>
          </div>
          <div class="info-item">
            <label>執行耗時</label>
            <span>{{ formatExecutionTime(batchIssuance.executionTimeMs) }}</span>
          </div>
        </div>
      </div>

      <!-- 執行結果 -->
      <div class="detail-section">
        <h2 class="section-title">執行結果</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>目標會員數</label>
            <span>{{ batchIssuance.totalMembers || 0 }} 人</span>
          </div>
          <div class="info-item">
            <label>成功數量</label>
            <span class="text-success">{{ batchIssuance.successCount || 0 }} 人</span>
          </div>
          <div class="info-item">
            <label>失敗數量</label>
            <span class="text-danger">{{ batchIssuance.failedCount || 0 }} 人</span>
          </div>
          <div class="info-item">
            <label>總發放點數</label>
            <span>{{ (batchIssuance.successCount || 0) * (batchIssuance.points || 0) }} 點</span>
          </div>
          <div v-if="batchIssuance.status === 'processing'" class="info-item">
            <label>執行進度</label>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: `${batchIssuance.progressPercentage || 0}%` }"></div>
              <span class="progress-text">{{ batchIssuance.progressPercentage || 0 }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按鈕 -->
      <div class="detail-actions">
        <button 
          v-if="batchIssuance.status === 'completed' || batchIssuance.status === 'failed'"
          class="btn btn-secondary" 
          @click="showDetails = !showDetails"
        >
          <i class="ri-file-list-line"></i>
          {{ showDetails ? '隱藏' : '查看' }}發放明細
        </button>
      </div>

      <!-- 發放明細 -->
      <div v-if="showDetails" class="detail-section">
        <h2 class="section-title">發放明細</h2>
        
        <!-- 搜尋列 -->
        <div class="details-toolbar">
          <div class="search-box">
            <input
              v-model="detailsSearchQuery"
              type="text"
              class="form-control"
              placeholder="搜尋會員姓名、電話或 Email..."
              @input="handleDetailsSearch"
            />
            <i class="ri-search-line"></i>
          </div>
        </div>

        <div v-if="detailsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>

        <div v-else-if="detailsError" class="error-state">
          <p class="error-message">{{ detailsError }}</p>
        </div>

        <table v-else>
          <thead>
            <tr>
              <th>會員姓名</th>
              <th>手機號碼</th>
              <th>Email</th>
              <th>狀態</th>
              <th>發放點數</th>
              <th>失敗原因</th>
              <th>通知結果</th>
              <th>發放時間</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="transactionDetails.length === 0">
              <td colspan="8" class="empty-state">沒有發放明細</td>
            </tr>
            <tr v-for="detail in transactionDetails" :key="detail.id">
              <td>{{ detail.memberName || '-' }}</td>
              <td>{{ detail.memberPhone || '-' }}</td>
              <td>{{ detail.memberEmail || '-' }}</td>
              <td>
                <span v-if="detail.status === 'success'" class="status-badge status-success">
                  <i class="ri-check-line"></i> 成功
                </span>
                <span v-else class="status-badge status-failed">
                  <i class="ri-close-line"></i> 失敗
                </span>
              </td>
              <td>
                <span v-if="detail.status === 'success'" class="text-success">+{{ detail.pointsIssued }}</span>
                <span v-else class="text-muted">0</span>
              </td>
              <td>
                <span v-if="detail.errorMessage" class="error-text">{{ detail.errorMessage }}</span>
                <span v-else>-</span>
              </td>
              <td>
                <div v-if="detail.notificationResults" class="notif-results">
                  <span
                    v-for="(res, ch) in detail.notificationResults"
                    :key="ch"
                    :class="['notif-badge', res.sent ? 'notif-ok' : 'notif-fail']"
                    :title="res.error || res.messageId || ''"
                  >
                    <i :class="chIcon(ch)"></i>
                    {{ chLabel(ch) }}
                    <i :class="res.sent ? 'ri-check-line' : 'ri-close-line'"></i>
                  </span>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
              <td>{{ formatDateTime(detail.createdAt) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- 分頁 -->
        <ListPagination
          v-if="detailsPagination && detailsPagination.totalPages > 1"
          :page="detailsPagination.page"
          :total-pages="detailsPagination.totalPages"
          :total="detailsPagination.totalItems"
          @change="changeDetailsPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'BatchPointsIssuanceDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次發放點數詳情');
    const batchIssuance = ref(null);
    const loading = ref(false);
    const error = ref(null);
    
    // 明細相關狀態
    const showDetails = ref(false);
    const transactionDetails = ref([]);
    const detailsLoading = ref(false);
    const detailsError = ref(null);
    const detailsPagination = ref(null);
    const detailsSearchQuery = ref('');
    const detailsSearchTimeout = ref(null);
    const isWixtarClient = ref(false);

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        }
      } catch (e) {
        console.error(e);
      }
    };

    const loadBatchIssuance = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: 1,
          pageSize: 100,
          type: 'points'
        };

        const response = await pointsService.getBatchIssuances(params);

        if (response && response.success && response.data) {
          // 從列表中找到對應的批次
          const found = (response.data.items || []).find(
            item => item.issuanceId === route.params.batchId || item.id === route.params.batchId
          );

          if (found) {
            batchIssuance.value = {
              batchId: found.issuanceId || found.id,
              points: found.pointsAmount || 0,
              reason: found.campaignName || '-',
              status: found.status,
              createdAt: found.createdAt,
              executedAt: found.executedAt,
              completedAt: found.completedAt,
              executionTimeMs: found.executionTimeMs,
              totalMembers: found.targetMemberCount || 0,
              successCount: found.successCount || 0,
              failedCount: found.failureCount || 0,
              progressPercentage: found.status === 'processing' 
                ? Math.round(((found.successCount || 0) + (found.failureCount || 0)) / (found.targetMemberCount || 1) * 100)
                : found.status === 'completed' ? 100 : 0
            };
            // 如果已完成或失敗，自動顯示明細
            if (batchIssuance.value.status === 'completed' || batchIssuance.value.status === 'failed') {
              showDetails.value = true;
              loadTransactionDetails();
            }
          } else {
            error.value = '找不到該批次發放點數記錄';
          }
        } else {
          error.value = response?.message || '獲取批次發放點數詳情失敗';
        }
      } catch (err) {
        console.error('獲取批次發放點數詳情失敗:', err);
        error.value = err.message || '獲取批次發放點數詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const loadTransactionDetails = async (page = 1) => {
      if (!showDetails.value || !batchIssuance.value) return;
      
      detailsLoading.value = true;
      detailsError.value = null;

      try {
        const params = {
          page,
          pageSize: 20
        };

        if (detailsSearchQuery.value) {
          params.search = detailsSearchQuery.value;
        }

        const response = await pointsService.getBatchIssueDetails(
          batchIssuance.value.batchId,
          params
        );

        if (response && response.success && response.data) {
          transactionDetails.value = response.data.items || [];
          detailsPagination.value = response.data.pagination;
        } else {
          detailsError.value = response?.message || '獲取發放明細失敗';
        }
      } catch (err) {
        console.error('獲取發放明細失敗:', err);
        detailsError.value = err.message || '獲取發放明細失敗';
      } finally {
        detailsLoading.value = false;
      }
    };

    const handleDetailsSearch = () => {
      if (detailsSearchTimeout.value) {
        clearTimeout(detailsSearchTimeout.value);
      }
      detailsSearchTimeout.value = setTimeout(() => {
        loadTransactionDetails(1);
      }, 500);
    };

    const changeDetailsPage = (page) => {
      loadTransactionDetails(page);
    };

    const handleBack = () => {
      router.push('/client/points/batch-issue');
    };

    const getStatusText = (status) => {
      const statusMap = {
        processing: '執行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };

    const formatExecutionTime = (ms) => {
      if (!ms || ms === 0) return '-';
      if (ms < 1000) return `${ms}ms`;
      if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
      return `${(ms / 60000).toFixed(2)}min`;
    };

    const chLabel = (ch) => ({ line: 'LINE', email: 'Email', sms: 'SMS' }[ch] || ch);
    const chIcon = (ch) => ({
      line: 'ri-line-fill',
      email: 'ri-mail-line',
      sms: 'ri-message-2-line'
    }[ch] || 'ri-notification-line');

    onMounted(async () => {
      await loadClientMemberSystem();
      loadBatchIssuance();
    });

    return {
      pageTitle,
      batchIssuance,
      loading,
      error,
      showDetails,
      transactionDetails,
      detailsLoading,
      detailsError,
      detailsPagination,
      detailsSearchQuery,
      handleBack,
      getStatusText,
      getStatusBadgeClass,
      formatDateTime,
      formatExecutionTime,
      handleDetailsSearch,
      changeDetailsPage,
      isWixtarClient,
      chLabel,
      chIcon
    };
  }
};
</script>

<style scoped>
.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #e74c3c;
  margin-bottom: 16px;
}

.detail-container {
  max-width: 1200px;
}

.detail-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.code-badge {
  background: #f5f7fa;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #2c3e50;
  display: inline-block;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge-info {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-success {
  background: #e8f5e9;
  color: #388e3c;
}

.badge-danger {
  background: #ffebee;
  color: #d32f2f;
}

.badge-secondary {
  background: #f5f5f5;
  color: #757575;
}

.text-success {
  color: #388e3c;
  font-weight: 600;
}

.text-danger {
  color: #d32f2f;
  font-weight: 600;
}

.detail-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: flex-start;
}

.detail-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  position: relative;
  width: 100%;
  height: 24px;
  background: #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 600;
  color: #333;
  z-index: 1;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn i {
  font-size: 16px;
}

.details-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box input {
  width: 100%;
  padding: 8px 36px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

table thead {
  background: #f8f9fa;
}

table th,
table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
}

table th {
  font-weight: 600;
  color: #666;
}

table tbody tr:hover {
  background: #f8f9fa;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 16px 0;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-success {
  background: #e8f5e9;
  color: #388e3c;
}

.status-failed {
  background: #ffebee;
  color: #d32f2f;
}

.text-muted {
  color: #999;
}

.error-text {
  color: #d32f2f;
  font-size: 13px;
}

/* 通知結果徽章 */
.notif-results {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.notif-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.notif-ok {
  background: #e6f7ee;
  color: #1a7a43;
}

.notif-fail {
  background: #fff0f0;
  color: #c0392b;
}
</style>
